/**
 * BreathProgramPanel — iter1052
 * 8-седмична програма за задържане на дъха, свързана с дихателните
 * упражнения от Mind-Body лабораторията. Цел: +50% от изходното време.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Loader2, Waves, ShieldAlert } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BreathProgramPanel = ({ isDark, onChanged }) => {
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [hold, setHold] = useState("");
  const [baseline, setBaseline] = useState("");

  useEffect(() => {
    axios.get(`${API}/respiratory/breath-program`, { timeout: 25000 })
      .then(({ data }) => {
        setP(data);
        setBaseline(data.suggested_baseline_sec ?? data.baseline_sec ?? "");
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const call = async (url, body) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}${url}`, body || {});
      setP(data);
      onChanged && onChanged();
    } finally { setBusy(false); }
  };

  if (loading) {
    return <div className="p-3" data-testid="breath-loading">
      <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />
    </div>;
  }
  if (!p) return null;
  const box = isDark ? "bg-slate-900/60" : "bg-white border border-slate-200";
  const ex = Object.fromEntries((p.exercises || []).map((e) => [e.key, e]));

  return (
    <div className={`mt-2 p-3 rounded-xl border-2 ${
      isDark ? "bg-slate-800/40 border-slate-700" : "bg-slate-50 border-slate-200"
    }`} data-testid="breath-panel">
      <div className="flex items-center gap-2 mb-2">
        <Waves className="w-4 h-4 text-emerald-500" />
        <div className="text-xs font-bold">Програма за дъха — 8 седмици</div>
        {p.active && (
          <span className="ml-auto text-[10px] font-extrabold tabular-nums" data-testid="breath-progress">
            {p.progress_pct >= 0 ? "+" : ""}{p.progress_pct}%
          </span>
        )}
      </div>
      <p className="text-[11px] opacity-85 mb-2" data-testid="breath-intro">{p.intro_bg}</p>

      <div className={`p-2 rounded-lg text-[10px] mb-2 ${
        p.variant === "apnea"
          ? (isDark ? "bg-amber-500/15 border border-amber-500/40" : "bg-amber-50 border border-amber-300")
          : box
      }`} data-testid="breath-variant">
        <div className="font-bold">{p.variant_bg}</div>
        {p.switch_reason_bg && <p className="opacity-90 mt-0.5">{p.switch_reason_bg}</p>}
      </div>

      {!p.active && (
        <div className="space-y-2" data-testid="breath-setup">
          {p.variant !== "apnea" && (
          <label className="text-[10px] opacity-80 block">
            Изходно задържане след нормално вдишване (сек)
            <input type="number" step="any" value={baseline}
                   onChange={(e) => setBaseline(e.target.value)}
                   data-testid="breath-baseline-input"
                   className={`w-full mt-0.5 px-2 py-1 rounded-lg text-[11px] ${
                     isDark ? "bg-slate-900 border border-slate-700" : "bg-white border border-slate-300"
                   }`} />
          </label>
          )}
          <button onClick={() => call("/respiratory/breath-program/start",
                                      { baseline_sec: baseline === "" ? null : Number(baseline) })}
                  disabled={busy} data-testid="breath-start"
                  className="w-full py-2 rounded-lg bg-emerald-600 text-white text-[11px] font-bold disabled:opacity-60">
            {busy ? "Стартирам…" : "Стартирай програмата"}
          </button>
        </div>
      )}

      {p.active && (
        <div className="space-y-2">
          <div className={`p-2.5 rounded-lg text-[10px] ${box}`} data-testid="breath-summary">
            {p.target_sec
              ? <>Изходно <strong>{p.baseline_sec}s</strong> → цел <strong>{p.target_sec}s</strong></>
              : <>Цел: тонус на горния дихателен път</>}
            {p.best_hold_sec ? <> · най-добро <strong>{p.best_hold_sec}s</strong></> : null}
            {" "}· седмица <strong>{p.current_week}/8</strong> · сесии {p.sessions_done}/{p.sessions_total}
          </div>

          <div className="space-y-1" data-testid="breath-weeks">
            {(p.weeks || []).map((w) => (
              <div key={w.week} data-testid={`breath-week-${w.week}`}
                   className={`p-2 rounded-lg text-[10px] ${
                     w.is_current
                       ? (isDark ? "bg-emerald-500/15 border border-emerald-500/40" : "bg-emerald-50 border border-emerald-300")
                       : box
                   }`}>
                <div className="flex items-center gap-2">
                  <span className="font-bold">Седмица {w.week}</span>
                  {w.hold_target_sec ? <span className="opacity-80">цел {w.hold_target_sec}s</span> : null}
                  <span className="ml-auto opacity-70">{w.done}/{w.sessions} сесии</span>
                  {w.best_hold_sec ? <span className="font-bold">{w.best_hold_sec}s</span> : null}
                </div>
                <div className="opacity-80 mt-0.5">
                  {ex[w.exercise] ? <><strong>{ex[w.exercise].title_bg}</strong> — {ex[w.exercise].how_bg}</> : null}
                </div>
                <div className="opacity-70 italic">{w.note_bg}</div>
              </div>
            ))}
          </div>

          <div className={`p-2.5 rounded-lg ${box}`} data-testid="breath-log">
            <div className="text-[10px] font-bold opacity-80 mb-1">
              Отбележи сесия за седмица {p.current_week}
            </div>
            <div className="flex gap-2">
              {p.variant !== "apnea" && (
              <input type="number" step="any" value={hold} placeholder="задържане (сек)"
                     onChange={(e) => setHold(e.target.value)}
                     data-testid="breath-hold-input"
                     className={`flex-1 px-2 py-1 rounded-lg text-[11px] ${
                       isDark ? "bg-slate-900 border border-slate-700" : "bg-white border border-slate-300"
                     }`} />
              )}
              <button onClick={() => { call("/respiratory/breath-program/session",
                                            { week: p.current_week,
                                              hold_sec: hold === "" ? null : Number(hold),
                                              exercise: (p.weeks.find((w) => w.is_current) || {}).exercise || "" });
                                       setHold(""); }}
                      disabled={busy} data-testid="breath-log-session"
                      className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-[11px] font-bold disabled:opacity-60">
                Готово
              </button>
            </div>
          </div>

          <button onClick={() => call("/respiratory/breath-program/end")}
                  disabled={busy} data-testid="breath-end"
                  className={`w-full py-2 rounded-lg text-[11px] font-bold ${
                    isDark ? "bg-slate-800 text-slate-200" : "bg-white border border-slate-300"
                  }`}>
            Приключи програмата
          </button>
        </div>
      )}

      <div className={`mt-2 p-2.5 rounded-lg text-[10px] ${box}`} data-testid="breath-exercises">
        <div className="font-bold opacity-80 mb-1">Упражненията в програмата</div>
        {(p.exercises || []).map((e) => (
          <div key={e.key} className="mb-1" data-testid={`breath-exercise-${e.key}`}>
            <strong>{e.title_bg}</strong> — {e.how_bg}
            <div className="opacity-80">{e.why_bg}</div>
          </div>
        ))}
        <button onClick={() => navigate("/dashboard/mindbody-lab")}
                data-testid="breath-mindbody-link"
                className={`mt-1 w-full text-left p-2 rounded-lg ${
                  isDark ? "bg-sky-500/10 hover:bg-sky-500/20" : "bg-sky-50 hover:bg-sky-100"
                }`}>
          <span className="font-bold">Mind-Body лаборатория →</span>
          <span className="opacity-80"> Същите упражнения с дозировка и доказателства</span>
        </button>
      </div>

      <div className={`mt-2 p-2.5 rounded-lg text-[10px] ${
        isDark ? "bg-rose-900/30 border border-rose-500/50" : "bg-rose-50 border border-rose-300"
      }`} data-testid="breath-safety">
        <div className="font-bold opacity-90 mb-1 flex items-center gap-1">
          <ShieldAlert className="w-3 h-3 text-rose-500" /> Безопасност — не е пожелателно
        </div>
        {(p.safety || []).map((s) => <div key={s} className="mb-0.5">• {s}</div>)}
      </div>
    </div>
  );
};

export default BreathProgramPanel;
